import { useUserProfileresetPasswordByMail } from "@/lib/api/requests/user-profile.requests";
import { Button } from "@/ui-components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/ui-components/ui/form";
import { Input } from "@/ui-components/ui/input";
import { Card, CardContent } from "@/ui-components/ui/card";
import { SectionHeader } from "@/util-components/page-layout/section-header";
import { zodResolver } from "@hookform/resolvers/zod";
import { KeyRound, Loader2, Shield, ArrowLeft } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Link } from "react-router-dom";
import { toast } from "@/lib/toast";
import { useState } from "react";

const ResetPassword = () => {
  const [isLoading, setIsLoading] = useState(false);
  
  const formSchema = z.object({
    email: z.string().email({ message: "Bitte gib eine gültige Email-Adresse ein" }).min(2, "Füge deine Email hinzu"),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
    },
  });

  const resetPasswordMutation = useUserProfileresetPasswordByMail();

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    setIsLoading(true);
    const req = {
      mail: data.email,
    };
    
    resetPasswordMutation.mutate(req, {
      onSuccess: () => {
        toast.success("E-Mail zum Zurücksetzen wurde gesendet");
        setIsLoading(false);
      },
      onError: (error) => {
        toast.error("E-Mail konnte nicht gesendet werden", { error });
        console.error(error);
        setIsLoading(false);
      }
    });
  };

  return (
    <div className="min-h-screen w-full bg-slate-50/50 flex flex-col items-center p-4 sm:p-8">
      <div className="w-full max-w-5xl space-y-12 animate-in fade-in duration-700 pt-8 sm:pt-16">
        
        {/* Header Logo */}
        <div className="flex flex-col items-center gap-8">
           <img src="/logo.png" alt="SportyPost Logo" className="h-10 md:h-12 object-contain opacity-80" />
        </div>

        <div className="max-w-md mx-auto w-full">
          <Card className="border-none shadow-2xl shadow-slate-200/50 bg-white/80 backdrop-blur-xl ring-1 ring-slate-200/50">
            <CardContent className="p-0">
              <div className="p-6 md:p-10 space-y-8">
                
                <div className="space-y-2">
                  <SectionHeader 
                    icon={KeyRound}
                    title="Passwort vergessen?"
                    description="Kein Problem. Gib deine E-Mail-Adresse ein und wir senden dir einen Link zum Zurücksetzen."
                  />
                </div>

                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-base">E-Mail Adresse</FormLabel>
                          <FormControl>
                            <Input 
                              placeholder="name@beispiel.de" 
                              {...field} 
                              className="h-12 text-lg shadow-sm border-slate-200 focus:border-blue-500 focus:ring-blue-500/20"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <div className="space-y-4 pt-2">
                        <Button 
                          type="submit" 
                          disabled={isLoading}
                          size="lg"
                          className="w-full h-12 text-base font-medium shadow-lg shadow-blue-500/20 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 transition-all hover:scale-[1.02] active:scale-[0.98]"
                        >
                          {isLoading ? (
                            <>
                              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                              Sende Link...
                            </>
                          ) : (
                            "Link anfordern"
                          )}
                        </Button>

                        <Link to="/login" className="block">
                            <Button 
                                variant="ghost" 
                                type="button"
                                className="w-full h-12 text-slate-500 hover:text-slate-900 hover:bg-slate-50"
                            >
                                <ArrowLeft className="mr-2 h-4 w-4" />
                                Zurück zum Login
                            </Button>
                        </Link>
                    </div>
                  </form>
                </Form>

              </div>
            </CardContent>
          </Card>
          
          <div className="mt-12 flex justify-center gap-6 opacity-60 text-center text-sm text-slate-500">
             <div className="flex flex-col items-center gap-2">
               <Shield className="w-5 h-5" />
               <span>Sicherer Prozess</span>
             </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
